import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { getOrganizationDetailsReduxRequest } from "@redux-requests/organization-details";
import { ContentPageHeader } from "../../common/content-page-header/content-page-header";
import { LoadingIndicator } from "../../common/loading-indicator/loading-indicator";
import { OrganizationDetailsComponent } from "./organization-details-component/organization-details-component";
import { CardContainer } from "../../common/card-container/card-container";
import { resources } from "@constants/hardcoded-strings";
import { ContentPageBody } from "../../common/content-page-body/content-page-body";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "@idw-types/state/application-state";
import { ORGANIZATION_DETAILS_REFERENCE_DATA_TYPES, OrganizationDetailsReferenceData } from "@constants/reference-data";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { setOrganizationDetailsIdentityProvidersReferenceData, setOrganizationDetailsIdentityProvidersReferenceDataLoading } from "@redux/actions/organization-details-actions";
import { getIdentityProviderDetailsReferenceDataReduxRequest } from "@redux-requests/identity-provider-details";
import { getAxnAdminUsersRequest } from "@requests/axnadmin-core";
import { AxnAdminUser } from "@idw-types/data/axn-admin-user";
import { AxnIdentityProvider } from "@idw-types/data/reference-data/axn";
import { addObjectsToArrayInCommonReferenceData } from "@redux/actions/common-reference-data-actions";
import { EntityStatusTypes } from "@enums/entity-status-types";

export const OrganizationDetailsContainer = () => {
    const dispatch = useDispatch();
    const { axnCompanyId } = useParams<{ axnCompanyId: string }>();
    const {
        isLoadingOrganizationConfig,
        organizationConfig,
        identityProviderReferenceData,
        userPermissions
    } = useSelector((state: ApplicationState) => ({
        isLoadingOrganizationConfig: state.organizationDetails.organization.isLoading,
        organizationConfig: state.organizationDetails.organization.organization,
        identityProviderReferenceData: state.organizationDetails.referenceData,
        userPermissions: state.session.session.userPermissions
    }));

    const [shouldLoadOrganizationConfig, setShouldLoadOrganizationConfig] = useState(true);
    const [isLoadingOrganizationUsers, setIsLoadingOrganizationUsers] = useState(false);
    const [shouldLoadOrganizationUsers, setShouldLoadOrganizationUsers] = useState(true);
    const referenceData = useReferenceDataSelector<OrganizationDetailsReferenceData>(ORGANIZATION_DETAILS_REFERENCE_DATA_TYPES);
    const [axnAdminUsers, setAxnAdminUsers] = useState<Array<AxnAdminUser>>(null);

    useEffect(() => {
        if (!isLoadingOrganizationConfig && shouldLoadOrganizationConfig) {
            setShouldLoadOrganizationConfig(false);
            getOrganizationDetailsReduxRequest(dispatch)(axnCompanyId);
            getCommonReferenceDataReduxRequest(dispatch)(
                ORGANIZATION_DETAILS_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.ORGANIZATION_DETAILS,
                CommonReferenceDataPageTypes.ORGANIZATION_DETAILS
            );
            getIdentityProviderDetailsReferenceDataReduxRequest(dispatch, setOrganizationDetailsIdentityProvidersReferenceDataLoading, setOrganizationDetailsIdentityProvidersReferenceData)();

        }
    }, [isLoadingOrganizationConfig, shouldLoadOrganizationConfig]);

    const handleNewIdpCreated = async (identityProvider: AxnIdentityProvider) => {
        dispatch(addObjectsToArrayInCommonReferenceData([...referenceData.axnIdentityProviders, identityProvider], "axnIdentityProviders"));
    }

    const getAxnAdminUsers = async (organizationID: string) => {
        setIsLoadingOrganizationUsers(true);
        const { data } = await getAxnAdminUsersRequest();

        if (data?.axnAdminUsers) {
            const filteredAxnAdminUsers = data.axnAdminUsers.filter(user => user.organizationID === organizationID && user.status === EntityStatusTypes.ACTIVE);
            setAxnAdminUsers(filteredAxnAdminUsers);
        }
        setIsLoadingOrganizationUsers(false);
    };

    useEffect(() => {
        //org config loaded and has a login service id - fetch the users
        if (organizationConfig?.loginServiceIDPID && !shouldLoadOrganizationConfig) {
            if (!isLoadingOrganizationUsers && !axnAdminUsers) {
                setShouldLoadOrganizationUsers(false);
                getAxnAdminUsers(organizationConfig.id);
            }
        }
        //org config loaded but no login service  is, don't fetch users
        else if (organizationConfig && !shouldLoadOrganizationConfig) {
            setShouldLoadOrganizationUsers(false);
        }
    }, [organizationConfig, isLoadingOrganizationUsers, shouldLoadOrganizationConfig]);


    const isIdpReferenceDataPresent = useMemo(() => {
        return !!(
            identityProviderReferenceData.categories &&
            identityProviderReferenceData.classifications &&
            identityProviderReferenceData.companies &&
            identityProviderReferenceData.loas &&
            identityProviderReferenceData.protocols &&
            identityProviderReferenceData.attributeTypes &&
            identityProviderReferenceData.attributeTypeFields &&
            identityProviderReferenceData.defaultScheme &&
            identityProviderReferenceData.schemes &&
            identityProviderReferenceData.urlTypes
        );
    }, [
        identityProviderReferenceData.categories,
        identityProviderReferenceData.classifications,
        identityProviderReferenceData.companies,
        identityProviderReferenceData.loas,
        identityProviderReferenceData.protocols,
        identityProviderReferenceData.attributeTypes,
        identityProviderReferenceData.attributeTypeFields,
        identityProviderReferenceData.defaultScheme,
        identityProviderReferenceData.schemes,
        identityProviderReferenceData.urlTypes
    ]);

    const isOrganizationDetailsReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.auth0IdentityProviders &&
            referenceData.axnIdentityProviders &&
            referenceData.customerSupportTiers
        );
    }, [
        referenceData.auth0IdentityProviders,
        referenceData.axnIdentityProviders,
        referenceData.customerSupportTiers
    ]);

    const isLoading = useMemo(() => {
        return (
            isLoadingOrganizationConfig ||
            referenceData.isLoading.organizationDetails ||
            identityProviderReferenceData.isLoading ||
            isLoadingOrganizationUsers ||
            shouldLoadOrganizationConfig ||
            shouldLoadOrganizationUsers
        );
    }, [
        isLoadingOrganizationConfig,
        referenceData.isLoading.organizationDetails,
        identityProviderReferenceData.isLoading,
        isLoadingOrganizationUsers,
        shouldLoadOrganizationConfig,
        shouldLoadOrganizationUsers
    ]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            organizationConfig &&
            isIdpReferenceDataPresent &&
            isOrganizationDetailsReferenceDataPresent &&
            (!organizationConfig.loginServiceIDPID || axnAdminUsers)
        );
    }, [
        isLoading,
        organizationConfig,
        axnAdminUsers,
        isIdpReferenceDataPresent,
        isOrganizationDetailsReferenceDataPresent
    ]);


    return (
        <CardContainer>
            <ContentPageHeader title={resources.pages.organizationDetails.header} />
            <ContentPageBody isMaxWidth={true}>
                {isLoaded && (
                    <OrganizationDetailsComponent
                        organizationConfig={organizationConfig}
                        axnIdentityProviders={referenceData.axnIdentityProviders}
                        identityProviderReferenceData={identityProviderReferenceData}
                        organizationUsers={axnAdminUsers}
                        onNewIdpCreated={handleNewIdpCreated}
                        setAxnAdminUsers={setAxnAdminUsers}
                        customerSupportTiers={referenceData.customerSupportTiers}
                        canCreateIdentityProviders={userPermissions.canCreateIdentityProviders}
                    />
                )}
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};